'use client';

import { useEffect, useRef, useState } from 'react';
import { COUNTDOWN_REFRESH_MS } from '../shared/constants';
import { msLeftToConfirm } from '../shared/confirm-deadline';

/**
 * Time left to confirm `startsAt`, redrawn every second, calling `onExpire` once it runs out.
 * `null` while no slot is being confirmed, which is what stops the interval on every other step.
 *
 * The clock is what lives in state; the remaining time is derived from it at render, and the
 * clock is re-read during the render that first sees a slot — so the very first frame already
 * shows the real countdown instead of a zero, or a reading left over from an earlier step.
 * `onExpire` is reached through a ref, so a caller that rebuilds the callback never restarts the
 * interval underneath it.
 */
export function useConfirmCountdown(startsAt: string | null, onExpire: () => void) {
  const [now, setNow] = useState(() => new Date());
  const [countingFor, setCountingFor] = useState(startsAt);
  const expire = useRef(onExpire);

  if (startsAt !== countingFor) {
    setCountingFor(startsAt);
    setNow(new Date());
  }

  useEffect(() => {
    expire.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    if (!startsAt) return;

    const timer = setInterval(() => setNow(new Date()), COUNTDOWN_REFRESH_MS);

    return () => clearInterval(timer);
  }, [startsAt]);

  const remainingMs = startsAt ? msLeftToConfirm(startsAt, now) : 0;

  useEffect(() => {
    if (startsAt && remainingMs <= 0) expire.current();
  }, [startsAt, remainingMs]);

  return remainingMs;
}
